import { catalogCazaLabel, isPublicZghartaCatalogItem } from '../domain';
import { getCategoryLabel } from '../config/categories';
import { getCanonicalUrl } from './seoRoutes';
import { publicCazaAttribution, publicItemDescription, publicJsonLdImage } from './publicMetadata';

export const ZGHARTA_CAZA_NAME = 'Zgharta Caza';
export const ZGHARTA_REGION_NAME = 'North Governorate';
export const ZGHARTA_COUNTRY_CODE = 'LB';

function toBoundaryCandidate(item) {
  return {
    ...item,
    village: item?.village || item?.location,
    villageAr: item?.villageAr || item?.locationAr,
    latitude: item?.coordinates?.lat,
    longitude: item?.coordinates?.lng,
  };
}

export function isZghartaCazaAttributable(item) {
  if (!item) return false;
  if (!isPublicZghartaCatalogItem(toBoundaryCandidate(item))) return false;
  return Boolean(item.village || item.location || item.coordinates?.lat != null || item.coordinates?.lng != null);
}

export function getCazaAttribution(item) {
  if (!isZghartaCazaAttributable(item)) return null;
  const attribution = publicCazaAttribution(item, 'en');
  if (!attribution.known) return null;
  return attribution.containedInPlace;
}

function compact(value) {
  return Object.entries(value).reduce((acc, [key, entry]) => {
    if (entry == null || entry === '' || (Array.isArray(entry) && entry.length === 0)) return acc;
    acc[key] = entry;
    return acc;
  }, {});
}

function localizedName(item, lang) {
  return lang === 'ar' ? (item.nameAr || item.name) : item.name;
}

function addressFor(item) {
  const attribution = publicCazaAttribution(item, 'en');
  const locality = item.village || item.location || attribution.locality;
  return compact({
    '@type': 'PostalAddress',
    addressLocality: locality,
    addressRegion: ZGHARTA_REGION_NAME,
    addressCountry: ZGHARTA_COUNTRY_CODE,
  });
}

function geoFor(item) {
  if (item.coordinates?.lat == null || item.coordinates?.lng == null) return null;
  return {
    '@type': 'GeoCoordinates',
    latitude: item.coordinates.lat,
    longitude: item.coordinates.lng,
  };
}

function businessSchemaType(item) {
  const category = item?.category;
  const mapped = {
    cafe: 'CafeOrCoffeeShop',
    hotel: 'Hotel',
    restaurant: 'Restaurant',
    shop: 'Store',
  }[category];
  if (mapped) return mapped;
  if (category === 'health') {
    const primaryType = item?.googlePrimaryType;
    if (primaryType === 'hospital') return 'Hospital';
    if (primaryType === 'pharmacy' || primaryType === 'drugstore') return 'Pharmacy';
    if (primaryType === 'dental_clinic' || primaryType === 'dentist') return 'Dentist';
    if (primaryType === 'doctor') return 'Physician';
    if (
      primaryType === 'medical_clinic'
      || primaryType === 'medical_center'
      || (typeof primaryType === 'string' && primaryType.endsWith('_clinic'))
    ) {
      return 'MedicalClinic';
    }
    return 'MedicalBusiness';
  }
  return 'LocalBusiness';
}

function basePlaceSchema(item, route, lang) {
  const attribution = getCazaAttribution(item);
  if (!attribution) return null;

  return compact({
    '@context': 'https://schema.org',
    '@id': `${getCanonicalUrl(route)}#place`,
    name: localizedName(item, lang),
    alternateName: item.nameAr && item.nameAr !== item.name ? item.nameAr : null,
    description: publicItemDescription(item, lang),
    image: publicJsonLdImage(item),
    url: getCanonicalUrl(route),
    address: addressFor(item),
    geo: geoFor(item),
    containedInPlace: attribution,
    areaServed: attribution,
    additionalProperty: [{
      '@type': 'PropertyValue',
      name: 'Lebanese caza',
      value: catalogCazaLabel(item?.caza, 'en'),
    }],
  });
}

export function buildTouristAttractionJsonLd(item, route, lang) {
  const base = basePlaceSchema(item, route, lang);
  if (!base) return null;
  return compact({
    ...base,
    '@type': 'TouristAttraction',
    touristType: getCategoryLabel(item.category, lang),
    openingHours: item.openHours,
  });
}

export function buildLocalBusinessJsonLd(item, route, lang) {
  const base = basePlaceSchema(item, route, lang);
  if (!base) return null;
  return compact({
    ...base,
    '@type': businessSchemaType(item),
    telephone: item.phone,
    priceRange: item.priceRange,
    servesCuisine: item.category === 'restaurant' || item.category === 'cafe'
      ? (item.specialties || ['Lebanese'])
      : null,
    sameAs: item.website ? [item.website] : [],
  });
}

export function buildEventJsonLd(item, route, lang) {
  const attribution = getCazaAttribution(item);
  if (!attribution) return null;

  const locationName = item.location || item.village || publicCazaAttribution(item, 'en').locality;
  return compact({
    '@context': 'https://schema.org',
    '@type': 'Event',
    '@id': `${getCanonicalUrl(route)}#event`,
    name: localizedName(item, lang),
    description: publicItemDescription(item, lang),
    startDate: item.date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    url: getCanonicalUrl(route),
    location: compact({
      '@type': 'Place',
      name: locationName,
      address: addressFor(item),
      containedInPlace: attribution,
    }),
    areaServed: attribution,
    organizer: {
      '@type': 'Organization',
      name: 'Zgharta Caza Tourism',
      url: 'https://zghartacaza.com',
    },
  });
}

export function buildJsonLdForRoute({ route, item, lang }) {
  if (!route?.itemType || !item) return null;
  if (route.itemType === 'place') return buildTouristAttractionJsonLd(item, route, lang);
  if (route.itemType === 'business') return buildLocalBusinessJsonLd(item, route, lang);
  if (route.itemType === 'event') return buildEventJsonLd(item, route, lang);
  return null;
}
